import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ConnectedPosition, Overlay, OverlayModule } from '@angular/cdk/overlay';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { AnimationOptions, LottieComponent } from 'ngx-lottie';
import { InfiniteScrollListItemDirective, DynamicVirtualViewportHeightDirective, TooltipModule } from '@libs/ui-lib';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs';
import { isNotNull } from '@libs/utils';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { TranslateModule } from '@ngx-translate/core';
import { AppointmentReason, AppointmentReasonList } from '@libs/store';
import { SvgIconComponent, SvgIcons } from '@ngneat/svg-icon';

const MINIMUM_SEARCH_LENGTH = 3;
const DEBOUNCE_TIME = 300;
const DEFAULT_HEIGHT = 444;

@Component({
  selector: 'lib-select-reason',
  imports: [
    CommonModule,
    LottieComponent,
    ReactiveFormsModule,
    OverlayModule,
    ScrollingModule,
    DynamicVirtualViewportHeightDirective,
    TranslateModule,
    InfiniteScrollListItemDirective,
    SvgIconComponent,
    TooltipModule,
  ],
  templateUrl: './select-reason.component.html',
  styleUrl: './select-reason.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectReasonComponent implements OnInit, AfterViewInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly overlay = inject(Overlay);
  private readonly cdr = inject(ChangeDetectorRef);

  public readonly innerControl = new FormControl<string>('');
  public readonly isInputFocused = signal(false);
  public readonly dropdownMaxHeight = signal(DEFAULT_HEIGHT);
  public readonly dropdownHeight = signal(DEFAULT_HEIGHT);
  public readonly dropdownOpen = signal(false);
  public readonly prefixIconSrc = signal<SvgIcons | null>(null);

  public readonly noResultLabelText = signal('');
  public readonly isReasonsDataLoading = signal(false);
  public readonly isLoading = signal(false);
  public isInvalidReason = signal(false);

  public readonly dropdownItems = signal<AppointmentReasonList | null>(null);
  public readonly filteredItems = signal<AppointmentReasonList | null>(null);
  public readonly selectedItems = signal<AppointmentReasonList | null>(null);

  public readonly scrollStrategy = this.overlay.scrollStrategies.noop();

  public readonly overlayPositionStrategy: ConnectedPosition[] = [
    {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'end',
      overlayY: 'top',
      offsetY: 4,
    },
    {
      originX: 'end',
      originY: 'top',
      overlayX: 'end',
      overlayY: 'bottom',
    },
  ];

  public lottieOptions: AnimationOptions = {
    path: '/assets/animations/loading-spinner.json',
  };

  @ViewChild('dropdownControl', { static: true, read: ElementRef }) public dropdownControlElement!: ElementRef<HTMLDivElement>;
  @ViewChild('input') public inputElement!: ElementRef<HTMLInputElement>;

  @Input() public set maxHeight(value: number) {
    this.dropdownMaxHeight.set(value);
  }

  @Input() public set prefixIcon(value: SvgIcons | null) {
    this.prefixIconSrc.set(value);
  }

  @Input() public set reasonsLoading(value: boolean) {
    this.isReasonsDataLoading.set(value);
  }

  @Input() public set loading(value: boolean) {
    this.isLoading.set(value);
  }

  @Input() public set options(value: AppointmentReasonList | undefined) {
    this.dropdownItems.set(value ?? null);
  }

  @Input() public set noResultLabel(value: string) {
    this.noResultLabelText.set(value);
  }

  @Input() public set defaultValue(value: AppointmentReasonList | undefined) {
    if (value) {
      this.selectedItems.set(value);
    }
  }

  @Input() public minimumSearchLength = MINIMUM_SEARCH_LENGTH;

  @Output() public readonly itemSelected = new EventEmitter<AppointmentReason>();
  @Output() public readonly itemDeselected = new EventEmitter<AppointmentReason>();

  public ngOnInit(): void {
    this.innerControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef), debounceTime(DEBOUNCE_TIME), filter(isNotNull), distinctUntilChanged())
      .subscribe((searchTerm) => {
        this.filteredItems.set(
          this.dropdownItems()?.filter(
            (item) =>
              item.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
              item.abbreviation.toLowerCase().includes(searchTerm.toLowerCase()) ||
              item.name.toLowerCase().includes(searchTerm.toLowerCase()),
          ) ?? [],
        );

        this.openDropdown();

        if (searchTerm.length < this.minimumSearchLength && this.dropdownOpen()) {
          this.closeDropdown();
        }
      });
  }

  public ngAfterViewInit(): void {
    this.inputElement.nativeElement.focus();
  }

  public onOverlayOutsideClick(event: MouseEvent): void {
    const clickTarget = event.target as HTMLElement;
    const isFilterCloseButtonClicked = clickTarget.closest('.dropdown-button');
    const isInputFieldClicked = this.dropdownControlElement.nativeElement.contains(clickTarget);

    if (isFilterCloseButtonClicked || isInputFieldClicked) {
      return;
    }
    event.preventDefault();
    this.closeDropdown();
  }

  public getDropdownContainerHeight(): number {
    const rect = this.dropdownControlElement.nativeElement.getBoundingClientRect().bottom;
    const windowHeight = window.innerHeight;
    return windowHeight - rect;
  }

  public setDropdownHeight(height: number): void {
    this.dropdownHeight.set(height);
    this.cdr.markForCheck();
  }

  public openDropdown(): void {
    if (
      this.dropdownOpen() ||
      !this.innerControl.value ||
      (this.innerControl.value && this.innerControl.value?.length < MINIMUM_SEARCH_LENGTH)
    ) {
      return;
    }
    this.setDropdownHeight(this.getDropdownContainerHeight());
    this.dropdownOpen.set(true);
  }

  public closeDropdown(): void {
    this.dropdownOpen.set(false);
  }

  public selectItem(item: AppointmentReason): void {
    this.itemSelected.emit(item);
    this.innerControl.setValue('');
    this.closeDropdown();
    this.inputElement.nativeElement.focus();
  }

  public deselectItem(item: AppointmentReason): void {
    this.itemDeselected.emit(item);
    this.closeDropdown();
    this.isInputFocused.set(false);
  }

  public isItemSelected(item: AppointmentReason): boolean {
    return !!this.selectedItems()?.find((selectedItem) => selectedItem.code === item.code);
  }

  public onFocus(): void {
    this.isInputFocused.set(true);
    this.isInvalidReason.set(false);
  }

  public onBlur(): void {
    this.isInputFocused.set(false);
    if (this.innerControl.value && this.innerControl.value.length > 0) {
      this.isInvalidReason.set(true);
    }
  }
}
